import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTest } from '../context/TestContext';
import { 
  FaCheckCircle, 
  FaUpload, 
  FaWifi, 
  FaBatteryFull,
  FaHome,
  FaRedo,
  FaClock,
  FaShieldAlt
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const EndContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
`;

const SuccessCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  margin-bottom: 30px;
`;

const SuccessIcon = styled(motion.div)`
  font-size: 80px;
  color: #10b981;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #ffffff, #e0e7ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 18px;
  opacity: 0.8;
  margin-bottom: 30px;
  line-height: 1.6;
`;

const UploadSection = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UploadTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 4px;
`;

const ProgressText = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
`;

const StatusIcon = styled.div`
  font-size: 16px;
  color: #10b981;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled(motion.button)`
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  `}
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  width: 100%;
  max-width: 600px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

const TestEndScreen = ({ onRestart }) => {
  const { testItems, actions } = useTest();
  const [uploadComplete, setUploadComplete] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);

  const completedTests = testItems.filter(item => item.videoRecorded).length;
  const totalDuration = testItems.reduce((sum, item) => sum + item.duration, 0);

  useEffect(() => {
    // Simuler le transfert des vidéos
    const uploadInterval = setInterval(() => {
      setCurrentProgress(prev => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setUploadComplete(true);
          toast.success('Transfert terminé avec succès !');
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(uploadInterval);
  }, []);

  const handleRestart = () => {
    actions.resetTest();
    onRestart();
  };

  return (
    <EndContainer>
      <SuccessCard
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <SuccessIcon
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <FaCheckCircle />
        </SuccessIcon>

        <Title>Test terminé avec succès !</Title>
        <Subtitle>
          Merci d'avoir utilisé Exam Video Recorder. Toutes les vidéos ont été enregistrées 
          et sont en cours de transfert vers nos serveurs sécurisés.
        </Subtitle>

        <StatsContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <StatCard>
            <StatNumber>{completedTests}</StatNumber>
            <StatLabel>Tests complétés</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{totalDuration}s</StatNumber>
            <StatLabel>Durée totale</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>9</StatNumber>
            <StatLabel>Exercices effectués</StatLabel>
          </StatCard>
        </StatsContainer>

        <UploadSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <UploadTitle>
            <FaUpload />
            Transfert des vidéos
          </UploadTitle>
          
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill
                initial={{ scaleX: 0 }}
                animate={{ scaleX: currentProgress / 100 }}
                transition={{ duration: 0.3 }}
              />
            </ProgressBar>
            <ProgressText>
              {uploadComplete ? 'Transfert terminé' : `Transfert en cours... ${currentProgress}%`}
            </ProgressText>
          </ProgressContainer>

          <StatusGrid>
            <StatusItem>
              <StatusIcon>
                <FaWifi />
              </StatusIcon>
              Connexion stable
            </StatusItem>
            <StatusItem>
              <StatusIcon>
                <FaBatteryFull />
              </StatusIcon>
              Batterie OK
            </StatusItem>
            <StatusItem>
              <StatusIcon>
                <FaShieldAlt />
              </StatusIcon>
              Chiffrement actif
            </StatusItem>
            <StatusItem>
              <StatusIcon>
                <FaClock />
              </StatusIcon>
              Temps réel
            </StatusItem>
          </StatusGrid>
        </UploadSection>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          style={{ marginBottom: '30px' }}
        >
          <p style={{ fontSize: '16px', opacity: 0.8, lineHeight: '1.6' }}>
            <strong>Important :</strong> Gardez la tablette allumée et connectée à Internet 
            jusqu'à la fin du transfert. Les données seront analysées par notre IA médicale 
            pour fournir un diagnostic préliminaire.
          </p>
        </motion.div>

        <ActionButtons
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <Button
            variant="primary"
            onClick={handleRestart}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaRedo />
            Nouveau test
          </Button>
          
          <Button
            variant="secondary"
            onClick={handleRestart}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaHome />
            Accueil
          </Button>
        </ActionButtons>
      </SuccessCard>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        style={{ textAlign: 'center', opacity: 0.6 }}
      >
        <p style={{ fontSize: '14px', marginBottom: '10px' }}>
          Développé avec ❤️ par AI-Stroke
        </p>
        <p style={{ fontSize: '12px' }}>
          Dispositif médical certifié CE - Version 1.0.0
        </p>
      </motion.div>
    </EndContainer>
  );
};

export default TestEndScreen; 